/**
 * GetStartedChecklist Component
 *
 * Fixed-position collapsible widget that tracks real user onboarding actions.
 * Shows at bottom-right of screen when triggered and not completed.
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, X, Rocket } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Progress,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
} from '@scio3/ui';
import { useAuthStore } from '@/stores/auth-store';
import { WistiaDialog } from '@/components/shared';
import { useGetStartedChecklist } from '../hooks/useGetStartedChecklist';
import { GetStartedStep } from './GetStartedStep';
import { GETTING_STARTED_VIDEO_ID } from '../constants';

export function GetStartedChecklist() {
  const { t } = useTranslation();
  const { state, loading, completeChecklist } = useGetStartedChecklist();
  const company = useAuthStore((s) => s.company);
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeStepId, setActiveStepId] = useState<string | null>(() => {
    const firstIncomplete = state.steps.find((s) => s.required && !s.completed);
    return firstIncomplete?.id ?? null;
  });
  const [showVideo, setShowVideo] = useState(false);

  // Find the next incomplete step (for collapsed view)
  const nextStep = useMemo(() => {
    return state.steps.find((s) => !s.completed) ?? null;
  }, [state.steps]);

  // Don't render if not visible (not triggered or already completed)
  if (!state.isVisible) {
    return null;
  }

  const handleComplete = async () => {
    await completeChecklist();
  };

  const toggleStep = (stepId: string) => {
    setActiveStepId((current) => (current === stepId ? null : stepId));
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 w-80" data-testid="get-started-checklist">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <Card className="border border-border shadow-sm dark:border-muted-foreground/40 dark:bg-accent">
          {/* Header with collapse toggle */}
          <CardHeader
            className={cn('p-4 pb-3', !isExpanded && 'cursor-pointer')}
            onClick={() => !isExpanded && setIsExpanded(true)}
          >
            <div className="flex items-center justify-between">
              <CollapsibleTrigger asChild>
                <button
                  data-testid="checklist-collapse-toggle"
                  type="button"
                  className="flex flex-1 items-center gap-2 text-left focus-visible:outline-none"
                  aria-label={isExpanded ? t('getStarted.collapse') : t('getStarted.expand')}
                >
                  <Rocket className="h-5 w-5 text-primary" />
                  <span className="text-sm font-semibold">{t('getStarted.title')}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {state.completedCount}/{state.totalSteps}
                  </span>
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </CollapsibleTrigger>
              {/* Close/dismiss button */}
              <Button
                data-testid="checklist-dismiss-btn"
                variant="ghost"
                size="sm"
                className="ml-2 h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  handleComplete();
                }}
                disabled={loading}
                aria-label={t('getStarted.dismiss')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {/* Progress bar */}
            <Progress value={state.progressPercent} className="mt-3 h-1.5" />

            {/* Next step hint when collapsed */}
            {!isExpanded && nextStep && (
              <p className="mt-2 text-xs">
                <span className="text-muted-foreground">{t('getStarted.nextStep')}</span>{' '}
                <span className="text-primary">{t(nextStep.titleKey)}</span>
              </p>
            )}
          </CardHeader>

          <CollapsibleContent>
            <CardContent className="p-4 pt-0">
              {/* Welcome message */}
              <p className="mb-3 text-xs text-muted-foreground">{t('getStarted.welcomeMessage')}</p>

              {/* Steps list */}
              <div className="space-y-1">
                {state.steps.map((step, index) => (
                  <GetStartedStep
                    key={step.id}
                    step={step}
                    index={index}
                    isActive={activeStepId === step.id}
                    onToggle={() => toggleStep(step.id)}
                    payment={company?.payment}
                  />
                ))}
              </div>

              {/* Need help section */}
              <div className="mt-3 flex flex-wrap items-center gap-1 text-xs">
                <span className="text-muted-foreground">{t('getStarted.needHelp')}</span>
                <Button
                  data-testid="checklist-watch-video-btn"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => setShowVideo(true)}
                >
                  {t('getStarted.watchVideo')}
                </Button>
              </div>

              {/* Complete button (shown when all required steps are done) */}
              {state.isComplete && (
                <Button
                  data-testid="checklist-all-set-btn"
                  className="mt-4 w-full"
                  size="sm"
                  onClick={handleComplete}
                  disabled={loading}
                >
                  {t('getStarted.completeButton')}
                </Button>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <WistiaDialog
        videoId={showVideo ? GETTING_STARTED_VIDEO_ID : null}
        onClose={() => setShowVideo(false)}
      />
    </div>
  );
}
